import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Printer, Receipt, MessageCircle, Clock, CheckCircle2, KeyRound, FileDown, Loader2 } from "lucide-react";
import { PrintEntry, PrintExit } from "@/components/PrintViews";
import EditOSDialog from "@/components/EditOSDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ServiceOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm } = useAuth();
  const [os, setOs] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [settings, setSettings] = useState({});
  const [editOpen, setEditOpen] = useState(false);
  const [wa, setWa] = useState(null); // { which: 'principal'|'recado', tpl: 'os_received'|'os_ready', pdf: bool }
  const [statusNotify, setStatusNotify] = useState(null); // { status, which: 'principal'|'recado' }
  const [pdfLoading, setPdfLoading] = useState(false);
  const [entryLoading, setEntryLoading] = useState(false);
  const [exitLoading, setExitLoading] = useState(false);
  const printMode = useRef(null);

  const load = async () => { const { data } = await api.get(`/service-orders/${id}`); setOs(data); };
  useEffect(() => {
    load();
    api.get("/settings/workflow-status").then((r) => setStatuses(r.data));
    api.get("/settings").then((r) => setSettings(r.data || {}));
  }, [id]);

  if (!os) return <div className="text-zinc-500">Carregando...</div>;
  const canEdit = hasPerm("service_orders.edit");
  const statusMap = Object.fromEntries(statuses.map((s) => [s.key, s]));
  const st = statusMap[os.status] || {};
  const client = os.client || {};
  const phone2 = os.customerPhone2 || client.phone2 || "";

  const changeStatus = async (status, notifyPhone) => {
    try {
      const { data } = await api.post(`/service-orders/${id}/status`, { status, notify_phone: notifyPhone });
      setOs({ ...data, client: os.client });
      if (data.whatsapp_reason === "skipped") {
        toast.success("Status atualizado (sem envio de WhatsApp)");
      } else if (data.whatsapp_sent) {
        toast.success("Status atualizado — cliente avisado pelo WhatsApp");
      } else {
        const reasons = {
          disabled: "envio automático desativado (ative em Configurações → Mensagens)",
          not_configured: "WhatsApp não configurado no servidor",
          no_phone: "número não preenchido",
          send_failed: "falha no envio ao WhatsApp",
          exception: "erro inesperado no envio",
        };
        const why = reasons[data.whatsapp_reason] || "mensagem não enviada";
        toast.error(`Status atualizado, mas WhatsApp não enviado: ${why}`);
      }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const promptStatus = (status) => setStatusNotify({ status, which: "principal" });
  const confirmStatus = (send) => {
    if (!statusNotify) return;
    const { status, which } = statusNotify;
    const phone = send ? (which === "recado" ? phone2 : os.customerPhone) : "";
    setStatusNotify(null);
    changeStatus(status, send ? (phone || "") : "");
  };
  const finalize = async () => {
    try { const { data } = await api.post(`/service-orders/${id}/finalize`); setOs({ ...data, client: os.client }); toast.success("OS finalizada"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doPrint = (mode) => { printMode.current = mode; setTimeout(() => window.print(), 100); };

  const blobError = async (e) => {
    try {
      if (e.response?.data instanceof Blob) {
        const txt = await e.response.data.text();
        try { return formatApiError(JSON.parse(txt).detail); } catch { return txt || "Erro ao gerar o arquivo."; }
      }
    } catch { /* ignore */ }
    return formatApiError(e.response?.data?.detail);
  };
  const openOrDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w || w.closed || typeof w.closed === "undefined") {
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 20000);
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/service-orders/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${os.number}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 20000);
    } catch (e) { toast.error(await blobError(e)); }
    finally { setPdfLoading(false); }
  };

  const openEntryReceipt = async () => {
    setEntryLoading(true);
    try {
      const res = await api.get(`/service-orders/${id}/entry-receipt?base=${encodeURIComponent(window.location.origin)}`, { responseType: "blob" });
      openOrDownload(res.data, `${os.number}-entrada.pdf`);
    } catch (e) { toast.error(await blobError(e)); }
    finally { setEntryLoading(false); }
  };

  const openExitReceipt = async () => {
    setExitLoading(true);
    try {
      const res = await api.get(`/service-orders/${id}/exit-receipt?base=${encodeURIComponent(window.location.origin)}`, { responseType: "blob" });
      openOrDownload(res.data, `${os.number}-retirada.pdf`);
    } catch (e) { toast.error(await blobError(e)); }
    finally { setExitLoading(false); }
  };

  const trackUrl = () => `${window.location.origin}/acompanhar?os=${encodeURIComponent(os.number)}&c=${encodeURIComponent(os.withdrawalCode || "")}`;
  const fillMsg = (tpl) => (tpl || "")
    .replace(/{cliente}/g, (os.customerName || "").split(" ")[0])
    .replace(/{numero}/g, os.number)
    .replace(/{aparelho}/g, `${os.device?.brand || ""} ${os.device?.model || ""}`.trim() || "aparelho")
    .replace(/{status}/g, st.label || os.status)
    .replace(/{codigo}/g, os.withdrawalCode || "")
    .replace(/{link}/g, trackUrl());

  const sendWa = () => {
    const phone = wa.which === "recado" ? phone2 : os.customerPhone;
    const num = (phone || "").replace(/\D/g, "");
    if (!num) { toast.error("Este número não está preenchido."); return; }
    const fallbacks = {
      os_received: "Olá {cliente}! Recebemos seu {aparelho} (OS {numero}).",
      os_ready: "Olá {cliente}! Seu {aparelho} (OS {numero}) está pronto para retirada.",
      os_status: "Olá {cliente}! Atualização da sua OS {numero}: agora está {status}. Acompanhe em tempo real: {link}",
    };
    const tpl = settings.whatsapp_templates?.[wa.tpl] || fallbacks[wa.tpl] || fallbacks.os_received;
    const msg = fillMsg(tpl);
    setWa(null);
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="os-detail-page">
      <div className="no-print">
        <button onClick={() => navigate("/app/os")} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 text-sm"><ArrowLeft size={16} /> Voltar</button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-mono-kpi font-extrabold alpha-gold-text">{os.number}</h1>
              <span className="text-xs px-3 py-1 rounded-full font-medium" style={{ background: `${st.color}22`, color: st.color }}>{st.label || os.status}</span>
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-gold/10 text-gold-bright font-mono-kpi" data-testid="withdrawal-code"><KeyRound size={12} />{os.withdrawalCode}</span>
            </div>
            <p className="text-zinc-400 mt-1">{os.customerName} · {os.customerPhone || "sem telefone"}{phone2 ? ` · recado ${phone2}` : ""}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canEdit && <Button data-testid="edit-os-btn" variant="outline" onClick={() => setEditOpen(true)} className="border-white/15 hover:bg-white/5">Editar</Button>}
            <Button data-testid="download-pdf-btn" variant="outline" onClick={downloadPdf} disabled={pdfLoading} className="border-gold/30 text-gold-bright hover:bg-gold/10">{pdfLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileDown size={16} className="mr-2" />}Baixar PDF</Button>
            <Button data-testid="entry-receipt-btn" variant="outline" onClick={openEntryReceipt} disabled={entryLoading} className="border-gold/30 text-gold-bright hover:bg-gold/10">{entryLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Receipt size={16} className="mr-2" />}Cupom entrada</Button>
            <Button data-testid="exit-receipt-btn" variant="outline" onClick={openExitReceipt} disabled={exitLoading} className="border-gold/30 text-gold-bright hover:bg-gold/10">{exitLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Receipt size={16} className="mr-2" />}Cupom saída</Button>
            <Button data-testid="print-entry-btn" variant="outline" onClick={() => doPrint("entry")} className="border-gold/30 text-gold-bright hover:bg-gold/10"><Printer size={16} className="mr-2" />Entrada</Button>
            <Button data-testid="print-exit-btn" variant="outline" onClick={() => doPrint("exit")} className="border-gold/30 text-gold-bright hover:bg-gold/10"><Receipt size={16} className="mr-2" />Saída</Button>
            <Button data-testid="whatsapp-btn" variant="outline" onClick={() => setWa({ which: "principal", tpl: os.readyForCheckout ? "os_ready" : "os_received" })} className="border-green-500/30 text-green-400 hover:bg-green-500/10"><MessageCircle size={16} className="mr-2" />WhatsApp</Button>
            {canEdit && !os.readyForCheckout && <Button data-testid="finalize-btn" onClick={finalize} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><CheckCircle2 size={16} className="mr-2" />Finalizar</Button>}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="alpha-card p-6">
              <h3 className="font-display font-semibold text-lg text-white mb-4">Cliente</h3>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Info label="Nome" value={os.customerName} />
                <Info label="Telefone principal" value={os.customerPhone || "—"} />
                <Info label="Telefone de recado" value={phone2 || "—"} />
                <Info label="CPF" value={client.cpf || "—"} mono />
                <Info label="E-mail" value={client.email || "—"} />
                <Info label="CEP" value={client.cep || "—"} />
                <Info label="Endereço" value={[client.logradouro || client.address, client.bairro || client.neighborhood, client.cidade || client.city, client.estado].filter(Boolean).join(", ") || "—"} />
              </div>
            </div>
            <div className="alpha-card p-6">
              <h3 className="font-display font-semibold text-lg text-white mb-4">Aparelho & Serviço</h3>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <Info label="Marca / Modelo" value={`${os.device?.brand} ${os.device?.model}`} />
                <Info label="Cor" value={os.device?.color || "—"} />
                <Info label="IMEI" value={os.device?.imei || "—"} mono />
                <Info label="Nota fiscal" value={os.requiresInvoice ? "Sim" : "Não"} />
                <div className="sm:col-span-2"><Info label="Defeito relatado" value={os.reportedIssue} /></div>
                <div className="sm:col-span-2"><Info label="Reparo efetuado" value={os.performedRepair || "—"} /></div>
              </div>
            </div>
            <div className="alpha-card p-6">
              <div className="flex items-center gap-2 mb-4"><Clock size={18} className="text-gold" /><h3 className="font-display font-semibold text-lg text-white">Linha do tempo</h3></div>
              <div className="space-y-4" data-testid="os-timeline">
                {(os.timeline || []).slice().reverse().map((t, i, arr) => (
                  <div key={`${t.timestamp}-${t.type || t.to || i}`} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="h-3 w-3 rounded-full mt-1" style={{ background: statusMap[t.to]?.color || "#D4AF37" }} />
                      {i < arr.length - 1 && <span className="w-px flex-1 bg-white/10 my-1" />}
                    </div>
                    <div className="pb-2">
                      <div className="text-white text-sm font-medium">{t.description}</div>
                      <div className="text-xs text-zinc-600">{t.actor_name} · {new Date(t.timestamp).toLocaleString("pt-BR")}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="alpha-card p-6">
              <h3 className="font-display font-semibold text-lg text-white mb-4">Valores</h3>
              <Row label="Custo do reparo" value={money(os.repairCost)} />
              <div className="border-t border-white/10 my-3" />
              <div className="flex justify-between items-center"><span className="text-zinc-300">Valor cobrado</span><span className="text-2xl font-mono-kpi font-extrabold text-gold-bright">{money(os.chargedAmount)}</span></div>
            </div>
            {canEdit && (
              <div className="alpha-card p-6">
                <h3 className="font-display font-semibold text-lg text-white mb-4">Alterar Status</h3>
                <Select onValueChange={promptStatus}>
                  <SelectTrigger data-testid="status-select" className="bg-[#0A0A0C] border-white/10 text-white"><SelectValue placeholder="Selecionar status" /></SelectTrigger>
                  <SelectContent className="bg-[#121217] border-gold/20 text-white">
                    {statuses.map((s) => <SelectItem key={s.key} value={s.key} className="focus:bg-gold/10"><span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: s.color }} />{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="print-area hidden print:block">
        {printMode.current === "exit"
          ? <PrintExit os={os} client={client} settings={settings} />
          : <PrintEntry os={os} client={client} settings={settings} />}
      </div>

      <Dialog open={!!wa} onOpenChange={(o) => !o && setWa(null)}>
        <DialogContent className="bg-[#121217] border-gold/20 text-white sm:max-w-md" data-testid="wa-dialog">
          <DialogHeader><DialogTitle>Enviar por WhatsApp</DialogTitle></DialogHeader>
          {wa && (
            <div className="space-y-4">
              <div>
                <div className="text-xs text-zinc-400 mb-1.5">Enviar para</div>
                <div className="flex gap-2">
                  <button data-testid="wa-num-principal" onClick={() => setWa({ ...wa, which: "principal" })} className={`flex-1 rounded-lg border p-2.5 text-sm ${wa.which === "principal" ? "border-gold/60 bg-gold/10 text-gold-bright" : "border-white/10 text-zinc-300"}`}>
                    Principal<br /><span className="text-xs text-zinc-500">{os.customerPhone || "—"}</span>
                  </button>
                  <button data-testid="wa-num-recado" disabled={!phone2} onClick={() => setWa({ ...wa, which: "recado" })} className={`flex-1 rounded-lg border p-2.5 text-sm disabled:opacity-40 ${wa.which === "recado" ? "border-gold/60 bg-gold/10 text-gold-bright" : "border-white/10 text-zinc-300"}`}>
                    Recado<br /><span className="text-xs text-zinc-500">{phone2 || "não cadastrado"}</span>
                  </button>
                </div>
              </div>
              <div>
                <div className="text-xs text-zinc-400 mb-1.5">Mensagem</div>
                <Select value={wa.tpl} onValueChange={(v) => setWa({ ...wa, tpl: v })}>
                  <SelectTrigger data-testid="wa-tpl" className="bg-[#0A0A0C] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121217] border-gold/20 text-white">
                    <SelectItem value="os_received" className="focus:bg-gold/10">Recebimento (entrada)</SelectItem>
                    <SelectItem value="os_ready" className="focus:bg-gold/10">Retirada (pronta)</SelectItem>
                    <SelectItem value="os_status" className="focus:bg-gold/10">Atualização de status (com link)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setWa(null)} className="border-white/15 bg-transparent text-zinc-200 hover:bg-white/5">Cancelar</Button>
                <Button data-testid="wa-send" onClick={sendWa} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><MessageCircle size={15} className="mr-1.5" />Enviar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editOpen && <EditOSDialog os={os} client={client} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}

      <Dialog open={!!statusNotify} onOpenChange={(o) => !o && setStatusNotify(null)}>
        <DialogContent className="bg-[#121217] border-gold/20 text-white sm:max-w-md" data-testid="status-notify-dialog">
          <DialogHeader><DialogTitle>Avisar o cliente por WhatsApp?</DialogTitle></DialogHeader>
          {statusNotify && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-400">Escolha para qual número enviar a atualização de status — ou clique em "Não enviar".</div>
              <div className="flex gap-2">
                <button data-testid="status-num-principal" onClick={() => setStatusNotify({ ...statusNotify, which: "principal" })} className={`flex-1 rounded-lg border p-2.5 text-sm ${statusNotify.which === "principal" ? "border-gold/60 bg-gold/10 text-gold-bright" : "border-white/10 text-zinc-300"}`}>
                  Principal<br /><span className="text-xs text-zinc-500">{os.customerPhone || "—"}</span>
                </button>
                <button data-testid="status-num-recado" disabled={!phone2} onClick={() => setStatusNotify({ ...statusNotify, which: "recado" })} className={`flex-1 rounded-lg border p-2.5 text-sm disabled:opacity-40 ${statusNotify.which === "recado" ? "border-gold/60 bg-gold/10 text-gold-bright" : "border-white/10 text-zinc-300"}`}>
                  Recado<br /><span className="text-xs text-zinc-500">{phone2 || "não cadastrado"}</span>
                </button>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" data-testid="status-skip-send" onClick={() => confirmStatus(false)} className="border-white/15 bg-transparent text-zinc-200 hover:bg-white/5">Não enviar</Button>
                <Button data-testid="status-confirm-send" disabled={statusNotify.which === "principal" ? !os.customerPhone : !phone2} onClick={() => confirmStatus(true)} className="text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><MessageCircle size={15} className="mr-1.5" />Enviar aviso</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value, mono }) => (<div><div className="text-xs text-zinc-500 mb-0.5">{label}</div><div className={`text-white ${mono ? "font-mono-kpi" : ""}`}>{value}</div></div>);
const Row = ({ label, value }) => (<div className="flex justify-between py-1.5 text-sm"><span className="text-zinc-400">{label}</span><span className="text-white font-mono-kpi">{value}</span></div>);
